import React, { useState, useCallback } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Unstable_Grid2';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';
// Import các thành phần cho Popup
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import LoadingButton from '@mui/lab/LoadingButton'; // <--- IMPORT MỚI

import Iconify from 'src/components/iconify'; 

// --- Import component Upload ---
import { Upload } from 'src/components/upload'; 
// -------------------------------------------

import useCaySamFilterOptions from 'src/utils/use-caysam-filter-options';

type Props = {
  isSelect?: boolean;
  onApply?: (criteria: { tinhTrang?: number; diem?: number; tuoi?: number; excludeLoggedThisMonth?: boolean }) => void;
  filteredCount?: number;
  onUpdateGrowth?: () => void;
  onClearFilter?: () => void;
  onImportFile?: (file: File) => Promise<void> | void; 
  loadingImport?: boolean; // <--- PROP MỚI: Trạng thái loading từ hook
};

export default function FilterCaySam({ 
  isSelect = false,
  onApply, 
  filteredCount = 0, 
  onUpdateGrowth, 
  onClearFilter,
  onImportFile,
  loadingImport = false
}: Props) { 
  const { loading, tinhTrangOptions, diemSucKhoeOptions, tuoicayOptions } = useCaySamFilterOptions();

  const [tinhTrang, setTinhTrang] = useState<number>(0);
  const [diem, setDiem] = useState<number>(0);
  const [tuoi, setTuoi] = useState<number>(0);
  
  // State quản lý Popup
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const excludeLoggedThisMonth = true;

  // --- Logic Filter (Giữ nguyên) ---
  const handleChangeFilter = (
    field: 'tinhTrang' | 'diem' | 'tuoi', 
    newValue: number
  ) => {
    if (field === 'tinhTrang') setTinhTrang(newValue);
    if (field === 'diem') setDiem(newValue);
    if (field === 'tuoi') setTuoi(newValue);

    const nextTinhTrang = field === 'tinhTrang' ? newValue : tinhTrang;
    const nextDiem = field === 'diem' ? newValue : diem;
    const nextTuoi = field === 'tuoi' ? newValue : tuoi;

    const criteria = {
      tinhTrang: nextTinhTrang === 0 ? undefined : nextTinhTrang,
      diem: nextDiem === 0 ? undefined : nextDiem,
      tuoi: nextTuoi === 0 ? undefined : nextTuoi,
      excludeLoggedThisMonth
    };

    onApply?.(criteria);
    
    if (nextTinhTrang === 0 && nextDiem === 0 && nextTuoi === 0) {
        onClearFilter?.();
    }
  };

  const handleClear = () => {
      setTinhTrang(0);
      setDiem(0);
      setTuoi(0);
      onClearFilter?.();
      onApply?.({ 
          tinhTrang: undefined, 
          diem: undefined, 
          tuoi: undefined, 
          excludeLoggedThisMonth 
      });
  }

  // --- Logic Popup & Upload ---
  
  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    // Không cho đóng khi đang upload
    if (!loadingImport) { 
        setOpenDialog(false);
    }
  };

  const handleDropPdf = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        alert('Vui lòng chỉ chọn file PDF');
        return;
      }
      const newFile = Object.assign(file, {
        preview: URL.createObjectURL(file),
      });
      setSelectedFile(newFile);
    }
  }, []);

  const handleDeletePdf = () => {
    setSelectedFile(null);
  };

  const handleSubmitImport = async () => {
    if (selectedFile && onImportFile) {
      // Gọi hàm từ Parent và đợi nó xử lý xong
      await onImportFile(selectedFile);
      
      // Sau khi xong thì reset và đóng
      handleDeletePdf();
      setOpenDialog(false); // Lưu ý: Parent nên xử lý đóng nếu thành công, ở đây ta đóng luôn
    }
  };

  // ------------------------------

  const hasFilter = tinhTrang !== 0 || diem !== 0 || tuoi !== 0;
  const canAddGrowth = hasFilter && filteredCount > 0;

  return ( 
    <Box>
      <Grid container spacing={2}>
        {/* ... (Giữ nguyên phần Select Box) ... */}
        <Grid xs={12} md={4}>   
          <FormControl fullWidth size="small">   
            <InputLabel>Tình trạng</InputLabel>
            <Select 
              disabled = {!isSelect}
              value={tinhTrang}
              label="Tình trạng"
              onChange={(e) => handleChangeFilter('tinhTrang', Number(e.target.value))}
            >   
              <MenuItem value={0}>Tất cả</MenuItem>
              {loading ? (  
                <MenuItem disabled>
                  <CircularProgress size={18} sx={{ mr: 1 }} /> Đang tải...
                </MenuItem> 
              ) : ( 
                tinhTrangOptions.map((opt) => (
                  <MenuItem key={opt.value} value={Number(opt.value)}>{opt.text}</MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </Grid>
          
        <Grid xs={12} md={4}>
          <FormControl fullWidth size="small"> 
            <InputLabel>Điểm sức khỏe</InputLabel>
            <Select
            disabled = {!isSelect}
              value={diem} 
              label="Điểm sức khỏe" 
              onChange={(e) => handleChangeFilter('diem', Number(e.target.value))}
            >
              <MenuItem value={0}>Tất cả</MenuItem>
              {loading ? (
                <MenuItem disabled> 
                  <CircularProgress size={18} sx={{ mr: 1 }} /> Đang tải...
                </MenuItem>
              ) : (
                diemSucKhoeOptions.map((opt) => (
                  <MenuItem key={opt.value} value={Number(opt.value)}>{opt.text}</MenuItem>  
                ))
              )}
            </Select>
          </FormControl> 
        </Grid> 

        <Grid xs={12} md={4}>
          <FormControl fullWidth size="small">
            <InputLabel>Tuổi cây</InputLabel>
            <Select
            disabled = {!isSelect}
              value={tuoi}  
              label="Tuổi cây"  
              onChange={(e) => handleChangeFilter('tuoi', Number(e.target.value))}
            >  
              <MenuItem value={0}>Tất cả</MenuItem>
              {loading ? (
                <MenuItem disabled>
                  <CircularProgress size={18} sx={{ mr: 1 }} /> Đang tải...
                </MenuItem>
              ) : (  
                tuoicayOptions.map((opt) => (
                  <MenuItem key={opt.value} value={Number(opt.value)}>{opt.text}</MenuItem>
                ))
              )}
            </Select> 
          </FormControl> 
        </Grid>
      </Grid>
      
      {hasFilter && filteredCount === 0 && (
        <Box sx={{ mt: 2, p: 2, bgcolor: 'error.lighter', borderRadius: 1, border: '1px solid', borderColor: 'error.main' }}>
          <Typography variant="body2" sx={{ color: 'error.dark', fontWeight: 'bold' }}>
            ❌ Không tìm thấy cây sâm nào phù hợp với điều kiện lọc
          </Typography>
        </Box>
      )}
      
      {/* Summary & actions */}  
      {hasFilter && (
        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ color: 'text.secondary', flex: 1 }}>
            Đang lọc:
            {tinhTrang !== 0 && ` Tình trạng = ${tinhTrangOptions.find((o: any) => Number(o.value) === tinhTrang)?.text},`}
            {diem !== 0 && ` Điểm sức khỏe = ${diemSucKhoeOptions.find((o: any) => Number(o.value) === diem)?.text},`}
            {tuoi !== 0 && ` Tuổi cây = ${tuoicayOptions.find((o: any) => Number(o.value) === tuoi)?.text}`}
            {` - Số cây: ${filteredCount}`}
          </Box>
           
          {/* Nút Mở Popup */}
          <Button 
            variant="outlined"
            color="info"
            startIcon={<Iconify icon="eva:cloud-upload-fill" />}
            onClick={handleOpenDialog}
            disabled={filteredCount === 0}
          >
            Import File PDF
          </Button>

          <Button 
            variant="contained" 
            onClick={() => { 
              onUpdateGrowth?.(); 
            }} 
            disabled={!canAddGrowth}
            color={filteredCount === 0 ? 'warning' : 'primary'}
            startIcon={<Iconify icon="solar:leaf-bold" />}
          >
           Thêm nhật ký {filteredCount > 0 && `(${filteredCount})`}
          </Button> 

          <Button variant="text" color="inherit" onClick={handleClear}>
           Xóa bộ lọc
          </Button>
        </Box>
      )}

      {/* --- DIALOG POPUP --- */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 2 }}>
            Import dữ liệu từ PDF
            <IconButton onClick={handleCloseDialog} size="small" disabled={loadingImport}>
                <Iconify icon="eva:close-fill" />
            </IconButton>
        </DialogTitle>
        
        <DialogContent dividers>
             {/* Logic hiển thị Upload/Preview giống phiên bản trước */}
             {!selectedFile && (
                <Upload
                    file={selectedFile}
                    onDrop={handleDropPdf}
                    onDelete={handleDeletePdf}
                    helperTitle="Kéo thả hoặc chọn file PDF"
                    thumbnail={false}
                    accept={{ 'application/pdf': [] }}
                    helperText={
                        <Typography variant="caption" sx={{ mt: 1, display: 'block', textAlign: 'center', color: 'text.disabled' }}>
                            *.pdf (Bắt buộc)
                        </Typography>
                    }
                    sx={{ '& .upload-zone': { height: 200, border: '2px dashed', borderColor: 'grey.300', borderRadius: 1 }}}
                />
            )}

            {selectedFile && ( 
                <Box sx={{ position: 'relative', width: '100%', height: '60vh', bgcolor: 'background.neutral', borderRadius: 1, overflow: 'hidden' }}>
                    <Box sx={{ position: 'absolute', top: 10, right: 20, zIndex: 9, bgcolor: 'rgba(255, 255, 255, 0.9)', borderRadius: '50%', boxShadow: 2 }}>
                        <IconButton color="error" onClick={handleDeletePdf} disabled={loadingImport}>
                            <Iconify icon="eva:trash-2-fill" />
                        </IconButton>
                    </Box>
                    <embed src={(selectedFile as any).preview} type="application/pdf" width="100%" height="100%" style={{ border: 'none' }} />
                </Box>
            )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={handleCloseDialog} color="inherit" disabled={loadingImport}>
                Hủy bỏ
            </Button>
            
            {/* Sử dụng LoadingButton để hiển thị trạng thái xoay khi đang gọi API */}
            <LoadingButton 
                onClick={handleSubmitImport} 
                variant="contained" 
                loading={loadingImport}
                disabled={!selectedFile}
                startIcon={<Iconify icon="eva:checkmark-circle-2-fill" />}
            >
                Xử lý File
            </LoadingButton>
        </DialogActions>
      </Dialog>
    </Box> 
  );
}